import {
  ISolution,
  Activity,
  IActivityDescription,
  getActivityDescription,
  getBeginningOfTheDay,
  getNextDay,
  Location,
  UserActivityCategoryEnum,
  CurrencyCode,
  CandidatePoiItem,
} from '@mobi-components/global-solution';
import {RecommendationContext} from '@/store/state/global.types';
import {IPolyline, IMarker} from '@/store/solution/solution-map-handlers';
import {formatPrices} from '@mobi-components/amex-bluesky-components';
import { ItineraryListModule } from '@/store/modules/itinerary-list';
import { useParams } from '@/router/router';

export interface IMarkerExt extends IMarker {
  type: string;
  place: {
    id: string;
    rating: number;
    name: string;
    description: string;
    img: string;
    price: string;
    tags: {name: string; isActive: boolean}[];
    numberOfTags: number;
    isSelected?: boolean;
    isRecommendation?: boolean;
    day?: string;
    dayDetails?: string;
    readonly isStay?: boolean;
  } | null;
}

export interface DragEndEvent {
  date: number;
  recommendation: RecommendationContext;
}

export function getavailableDatesForPoi(poi: Activity, dates: Date[]): Date[] {
  return dates.filter((date) => {
    const dayStart = getBeginningOfTheDay(date);
    const dayEnd = getNextDay(date);
    const startTime = new Date(poi.scheduled_start_time * 1000);
    return startTime >= dayStart && startTime < dayEnd;
  });
}

export function getDatePois(pois: Activity[], date: Date): CandidatePoiItem[] {
  const dayStart = getBeginningOfTheDay(date);
  const dayEnd = getNextDay(date);

  return pois
    .filter((x) => {
      const start = x.scheduled_start_time;

      const startTime = new Date(start * 1000);
      return startTime >= dayStart && startTime < dayEnd;
    })
    .map((x) => {
      return x.source.content as CandidatePoiItem;
    });
}

export function isActivitOnTheDate(activity: Activity | null, date: Date): boolean {
  if (!activity) return false;
  const dayStart = getBeginningOfTheDay(date);
  const dayEnd = getNextDay(date);
  const startTime = new Date(activity.scheduled_start_time * 1000);
  const endTime = new Date(activity.scheduled_end_time * 1000);
  return (startTime >= dayStart && startTime < dayEnd) || (endTime >= dayStart && endTime < dayEnd);
}

export function getDateActivities(
  solution: ISolution | null,
  date: Date,
  additionalActivities: Activity[] = [],
): IActivityDescription[] {
  if (!solution) {
    return [];
  }
  const included = new Set();
  const dayActivities = (additionalActivities.length ? additionalActivities : solution.activities)
    .filter((a: Activity) => {
      if (included.has(a.id)) {
        return false;
      }
      included.add(a.id);
      return true;
    })

    .filter((x) => {
      return isActivitOnTheDate(x, date);
    });

  const activitiesForOneDay = dayActivities.map((x) => getActivityDescription(solution, x));

  const firstDayActivity = activitiesForOneDay.length > 0 ? activitiesForOneDay[0] : null;
  const lastDayActivity = activitiesForOneDay.length > 0 ? activitiesForOneDay.slice(-1)[0] : null;
  if (!firstDayActivity || !lastDayActivity) {
    return [];
  }
  return activitiesForOneDay;
}

export function getPolylinesForTheDate(
  solution: ISolution | null,
  map: google.maps.Map | null,
  date: Date,
  color = '#0C414C',
): IPolyline[] {
  if (!solution || !map) {
    return [];
  }

  const activities = getDateActivities(solution, date);

  return activities
    .filter((x: IActivityDescription) => {
      return x.activity.start_location !== x.activity.end_location && x.activity.polyline?.length > 0;
    })
    .map((i: IActivityDescription) => {
      const path = i.activity.polyline
        .split(',')
        .reduce((res: google.maps.LatLngLiteral[], latLng: string, index: number) => {
          if (index % 2 === 0) {
            const point: google.maps.LatLngLiteral = {
              lat: parseFloat(latLng),
              lng: 0,
            };
            res.push(point);
          } else {
            const point: google.maps.LatLngLiteral = res.pop() as google.maps.LatLngLiteral;
            point.lng = parseFloat(latLng);
            res.push(point);
          }
          return res;
        }, []);

      return [
        {
          id: i.activity.id,
          polyline: new google.maps.Polyline({
            path,
            strokeColor: color,
            strokeOpacity: 1,
            strokeWeight: 4,
            clickable: false,
            draggable: false,
            map,
            zIndex: 2,
          }),
        },
        {
          id: i.activity.id,
          polyline: new google.maps.Polyline({
            path,
            strokeColor: '#fff',
            strokeOpacity: 1,
            strokeWeight: 12,
            clickable: false,
            draggable: false,
            map,
            zIndex: 1,
          }),
        },
        {
          id: i.activity.id,
          polyline: new google.maps.Polyline({
            path,
            strokeColor: '#000',
            strokeOpacity: 0.02,
            strokeWeight: 20,
            clickable: false,
            draggable: false,
            map,
            zIndex: 0,
          }),
        },
      ];
    })
    .flat();
}

export const getFormattedPoiCardDayDescription = (fullName: string, type: string, day: string) => {
  const cutName = (fullName || '').replace('LUNCH:', '').replace('BREAKFAST:', '').replace('DINNER:', '').trim();
  const typeName = (fullName || '').replace(cutName, '').replace(':', '').trim().toLowerCase();
  const lowerType = type.toLocaleLowerCase();
  const formattedType = (lowerType && lowerType.charAt(0).toUpperCase() + lowerType.slice(1)) || '';
  const formattedTypeName = (typeName && typeName.charAt(0).toUpperCase() + typeName.slice(1)) || '';
  const readableType = formattedTypeName || formattedType;

  return `${readableType} ${day}`.trim();
};


function mapActivityToTheMarker(
  x: Activity,
  // recommendations: RecommendationContext[],
  solution: ISolution,
): IMarkerExt {
  // const recommendation = recommendations.find((y) => y.id === x.source?.candidate_id);

  // if (recommendation) {
  //   return {
  //     id: x.id,
  //     place: x,
  //     type: x.category,
  //     position: recommendation.location,
  //     klass: '',
  //   };
  // }

  const location = solution.locations.find((l: Location) => l.id === x.start_location);
  const isSelected = solution.activities.some((a) => a.id === x.id) && !x.spec?.curated;
  // const activityDescription = {activity: x, startLocation: location!, endLocation: location!};
  // const activityRecommendation = getRecommendationContextFromActivity(activityDescription, recommendations);
  if (
    (x.category && x.category === UserActivityCategoryEnum.FLIGHT) ||
    (x.category && x.category === UserActivityCategoryEnum.RAIL)
  ) {
    return {
      id: x.id,
      place: null,
      type: x.category,
      position: {
        lat: location?.lat || 0,
        lng: location?.lng || 0,
      },
      klass: '',
    };
  }
  const params = useParams();
  const poi = x.source?.content || x;
  const day = ItineraryListModule.getDayById(x.id, params.value.contextName || '');
  const dayDetails = getFormattedPoiCardDayDescription(poi.name, x.category, day?.label || '');
  return {
    id: x.id,
    place: {
      ...poi,
      img: x.images.medium,
      numberOfTags: (x.types || []).length,
      price: formatPrices(x.prices).labels[CurrencyCode.USD] || '',
      tags: [...new Set(x.types)].map((t) => ({name: t, isActive: false})),
      isSelected,

      dayDetails,
      day: day?.label,

      isRecommendation: x.spec?.curated,
      get isStay() {
        return x.category === UserActivityCategoryEnum.ACCOMMODATION;
      },
    },
    type: x.category,
    position: {
      lat: location?.lat || 0,
      lng: location?.lng || 0,
    },
    klass: '',
  };
}

export function getMarkers(
  solution: ISolution | null,
  category: UserActivityCategoryEnum | null = null,
  date: Date | null = null,
  notCurated = true,
): IMarkerExt[] {
  if (!solution) {
    return [];
  }
  let allActivities = [...solution.activities];

  let firstFlight =
    category === UserActivityCategoryEnum.FLIGHT
      ? allActivities.find((x) => x.category === UserActivityCategoryEnum.FLIGHT)
      : null;

  let lastFlight =
    category === UserActivityCategoryEnum.FLIGHT
      ? allActivities.reverse().find((x) => x.category === UserActivityCategoryEnum.FLIGHT)
      : null;

  if (date) {
    allActivities = getDateActivities(solution, date).map((x) => x.activity);
    lastFlight = isActivitOnTheDate(lastFlight || null, date) ? lastFlight : null;
    firstFlight = isActivitOnTheDate(firstFlight || null, date) ? firstFlight : null;
  }

  if (firstFlight) {
    firstFlight.start_location = firstFlight.end_location;
  }
  if (lastFlight) {
    lastFlight.end_location = lastFlight.start_location;
  }

  const activities = new Set();
  return allActivities
    .filter((x: Activity) => {
      const isReal = notCurated ? !x.spec?.curated : x.spec?.curated;
      return (
        isReal &&
        (!category || x.category === category) &&
        x.category !== UserActivityCategoryEnum.CONTEXT &&
        (x.start_location === x.end_location || x.category === UserActivityCategoryEnum.FLIGHT) &&
        !x.mode &&
        solution.locations.some((l: Location) => l.id === x.start_location)
      );
    })
    .concat(lastFlight ? [lastFlight] : [])
    .concat(firstFlight ? [firstFlight] : [])
    .filter((a: Activity) => {
      const included = activities.has(a.id);
      activities.add(a.id);
      return !included;
    })
    .map((x: Activity) => {
      return mapActivityToTheMarker(x, solution);
    });
}
